/*!
 * devextreme-angular
 * Version: 19.1.3
 * Build date: Tue May 14 2019
 *
 * Copyright (c) 2012 - 2019 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */

/* tslint:disable:max-line-length */











import { BrowserTransferStateModule } from '@angular/platform-browser';
import { TransferState } from '@angular/platform-browser';

import {
    Component,
    NgModule,
    ElementRef,
    NgZone,
    PLATFORM_ID,
    Inject,

    Input,
    Output,
    OnDestroy,
    EventEmitter
} from '@angular/core';


import DevExpress from 'devextreme/bundles/dx.all';

import DxToast from 'devextreme/ui/toast';


import { DxComponent } from '../core/component';
import { DxTemplateHost } from '../core/template-host';
import { DxIntegrationModule } from '../core/integration';
import { DxTemplateModule } from '../core/template';
import { NestedOptionHost } from '../core/nested-option';
import { WatcherHelper } from '../core/watcher-helper';


import { DxoAnimationModule } from './nested/animation';
import { DxoHideModule } from './nested/hide';
import { DxoShowModule } from './nested/show';
import { DxoPositionModule } from './nested/position';
import { DxoAtModule } from './nested/at';
import { DxoBoundaryOffsetModule } from './nested/boundary-offset';
import { DxoCollisionModule } from './nested/collision';
import { DxoMyModule } from './nested/my';
import { DxoOffsetModule } from './nested/offset';





/**
 * The Toast is a widget that provides pop-up notifications.
 */
@Component({
    selector: 'dx-toast',
    template: '',
    providers: [
        DxTemplateHost,
        WatcherHelper,
        NestedOptionHost
    ]
})
export class DxToastComponent extends DxComponent implements OnDestroy {
    instance: DxToast;


    /**
     * Specifies the shortcut key that sets focus on the widget.
     */
    @Input()
    get accessKey(): string {
        return this._getOption('accessKey');
    }
    set accessKey(value: string) {
        this._setOption('accessKey', value);
    }


    /**
     * Configures widget visibility animations. This object contains two fields: show and hide.
     */
    @Input()
    get animation(): { hide?: DevExpress.animationConfig, show?: DevExpress.animationConfig } {
        return this._getOption('animation');
    }
    set animation(value: { hide?: DevExpress.animationConfig, show?: DevExpress.animationConfig }) {
        this._setOption('animation', value);
    }


    /**
     * A Boolean value specifying whether or not the widget is closed if a user presses the Back hardware button.
     */
    @Input()
    get closeOnBackButton(): boolean {
        return this._getOption('closeOnBackButton');
    }
    set closeOnBackButton(value: boolean) {
        this._setOption('closeOnBackButton', value);
    }


    /**
     * A Boolean value specifying whether or not the toast is closed if a user clicks it.
     */
    @Input()
    get closeOnClick(): boolean {
        return this._getOption('closeOnClick');
    }
    set closeOnClick(value: boolean) {
        this._setOption('closeOnClick', value);
    }


    /**
     * Specifies whether to close the widget if a user clicks outside it.
     */
    @Input()
    get closeOnOutsideClick(): boolean | Function {
        return this._getOption('closeOnOutsideClick');
    }
    set closeOnOutsideClick(value: boolean | Function) {
        this._setOption('closeOnOutsideClick', value);
    }


    /**
     * A Boolean value specifying whether or not the toast is closed if a user swipes it out of the screen boundaries.
     */
    @Input()
    get closeOnSwipe(): boolean {
        return this._getOption('closeOnSwipe');
    }
    set closeOnSwipe(value: boolean) {
        this._setOption('closeOnSwipe', value);
    }


    /**
     * Specifies a custom template for the widget content.
     */
    @Input()
    get contentTemplate(): any {
        return this._getOption('contentTemplate');
    }
    set contentTemplate(value: any) {
        this._setOption('contentTemplate', value);
    }


    /**
     * Specifies whether to render the widget's content when it is displayed. If false, the content is rendered immediately.
     */
    @Input()
    get deferRendering(): boolean {
        return this._getOption('deferRendering');
    }
    set deferRendering(value: boolean) {
        this._setOption('deferRendering', value);
    }


    /**
     * The time span in milliseconds during which the Toast widget is visible.
     */
    @Input()
    get displayTime(): number {
        return this._getOption('displayTime');
    }
    set displayTime(value: number) {
        this._setOption('displayTime', value);
    }


    /**
     * Specifies the attributes to be attached to the widget's root element.
     */
    @Input()
    get elementAttr(): any {
        return this._getOption('elementAttr');
    }
    set elementAttr(value: any) {
        this._setOption('elementAttr', value);
    }


    /**
     * Specifies whether the widget can be focused using keyboard navigation.
     */
    @Input()
    get focusStateEnabled(): boolean {
        return this._getOption('focusStateEnabled');
    }
    set focusStateEnabled(value: boolean) {
        this._setOption('focusStateEnabled', value);
    }


    /**
     * Specifies the widget's height in pixels.
     */
    @Input()
    get height(): number | Function | string {
        return this._getOption('height');
    }
    set height(value: number | Function | string) {
        this._setOption('height', value);
    }


    /**
     * Specifies text for a hint that appears when a user pauses on the widget.
     */
    @Input()
    get hint(): string {
        return this._getOption('hint');
    }
    set hint(value: string) {
        this._setOption('hint', value);
    }


    /**
     * Specifies whether the widget changes its state when a user pauses on it.
     */
    @Input()
    get hoverStateEnabled(): boolean {
        return this._getOption('hoverStateEnabled');
    }
    set hoverStateEnabled(value: boolean) {
        this._setOption('hoverStateEnabled', value);
    }


    /**
     * Specifies the maximum height the widget can reach while resizing.
     */
    @Input()
    get maxHeight(): number | Function | string {
        return this._getOption('maxHeight');
    }
    set maxHeight(value: number | Function | string) {
        this._setOption('maxHeight', value);
    }


    /**
     * Specifies the maximum width the widget can reach while resizing.
     */
    @Input()
    get maxWidth(): number | Function | string {
        return this._getOption('maxWidth');
    }
    set maxWidth(value: number | Function | string) {
        this._setOption('maxWidth', value);
    }


    /**
     * The Toast message text.
     */
    @Input()
    get message(): string {
        return this._getOption('message');
    }
    set message(value: string) {
        this._setOption('message', value);
    }


    /**
     * Specifies the minimum height the widget can reach while resizing.
     */
    @Input()
    get minHeight(): number | Function | string {
        return this._getOption('minHeight');
    }
    set minHeight(value: number | Function | string) {
        this._setOption('minHeight', value);
    }


    /**
     * Specifies the minimum width the widget can reach while resizing.
     */
    @Input()
    get minWidth(): number | Function | string {
        return this._getOption('minWidth');
    }
    set minWidth(value: number | Function | string) {
        this._setOption('minWidth', value);
    }


    /**
     * Positions the widget.
     */
    @Input()
    get position(): DevExpress.positionConfig | string {
        return this._getOption('position');
    }
    set position(value: DevExpress.positionConfig | string) {
        this._setOption('position', value);
    }


    /**
     * Switches the widget to a right-to-left representation.
     */
    @Input()
    get rtlEnabled(): boolean {
        return this._getOption('rtlEnabled');
    }
    set rtlEnabled(value: boolean) {
        this._setOption('rtlEnabled', value);
    }


    /**
     * Specifies whether to shade the container when the widget is active.
     */
    @Input()
    get shading(): boolean {
        return this._getOption('shading');
    }
    set shading(value: boolean) {
        this._setOption('shading', value);
    }


    /**
     * Specifies the shading color.
     */
    @Input()
    get shadingColor(): string {
        return this._getOption('shadingColor');
    }
    set shadingColor(value: string) {
        this._setOption('shadingColor', value);
    }


    /**
     * Specifies the number of the element when the Tab key is used for navigating.
     */
    @Input()
    get tabIndex(): number {
        return this._getOption('tabIndex');
    }
    set tabIndex(value: number) {
        this._setOption('tabIndex', value);
    }


    /**
     * Specifies the Toast widget type.
     */
    @Input()
    get type(): string {
        return this._getOption('type');
    }
    set type(value: string) {
        this._setOption('type', value);
    }


    /**
     * A Boolean value specifying whether or not the widget is visible.
     */
    @Input()
    get visible(): boolean {
        return this._getOption('visible');
    }
    set visible(value: boolean) {
        this._setOption('visible', value);
    }


    /**
     * Specifies the widget's width in pixels.
     */
    @Input()
    get width(): number | Function | string {
        return this._getOption('width');
    }
    set width(value: number | Function | string) {
        this._setOption('width', value);
    }

    /**
     * A function that is executed when the widget's content is ready and each time the content is changed.
     */
    @Output() onContentReady: EventEmitter<any>;

    /**
     * A function that is executed before the widget is disposed of.
     */
    @Output() onDisposing: EventEmitter<any>;

    /**
     * A function that is executed after the widget is hidden.
     */
    @Output() onHidden: EventEmitter<any>;

    /**
     * A function that is executed before the widget is hidden.
     */
    @Output() onHiding: EventEmitter<any>;

    /**
     * A function that is executed only once, after the widget is initialized.
     */
    @Output() onInitialized: EventEmitter<any>;

    /**
     * A function that is executed after a widget option is changed.
     */
    @Output() onOptionChanged: EventEmitter<any>;

    /**
     * A function that is executed before the widget is displayed.
     */
    @Output() onShowing: EventEmitter<any>;

    /**
     * A function that is executed after the widget is displayed.
     */
    @Output() onShown: EventEmitter<any>;

    /**
     * A handler for the accessKeyChange event.
     */
    @Output() accessKeyChange: EventEmitter<string>;

    /**
     * A handler for the animationChange event.
     */
    @Output() animationChange: EventEmitter<{ hide?: DevExpress.animationConfig, show?: DevExpress.animationConfig }>;

    /**
     * A handler for the closeOnBackButtonChange event.
     */
    @Output() closeOnBackButtonChange: EventEmitter<boolean>;

    /**
     * A handler for the closeOnClickChange event.
     */
    @Output() closeOnClickChange: EventEmitter<boolean>;

    /**
     * A handler for the closeOnOutsideClickChange event.
     */
    @Output() closeOnOutsideClickChange: EventEmitter<boolean | Function>;

    /**
     * A handler for the closeOnSwipeChange event.
     */
    @Output() closeOnSwipeChange: EventEmitter<boolean>;

    /**
     * A handler for the contentTemplateChange event.
     */
    @Output() contentTemplateChange: EventEmitter<any>;

    /**
     * A handler for the deferRenderingChange event.
     */
    @Output() deferRenderingChange: EventEmitter<boolean>;

    /**
     * A handler for the displayTimeChange event.
     */
    @Output() displayTimeChange: EventEmitter<number>;

    /**
     * A handler for the elementAttrChange event.
     */
    @Output() elementAttrChange: EventEmitter<any>;

    /**
     * A handler for the focusStateEnabledChange event.
     */
    @Output() focusStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the heightChange event.
     */
    @Output() heightChange: EventEmitter<number | Function | string>;

    /**
     * A handler for the hintChange event.
     */
    @Output() hintChange: EventEmitter<string>;

    /**
     * A handler for the hoverStateEnabledChange event.
     */
    @Output() hoverStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the maxHeightChange event.
     */
    @Output() maxHeightChange: EventEmitter<number | Function | string>;

    /**
     * A handler for the maxWidthChange event.
     */
    @Output() maxWidthChange: EventEmitter<number | Function | string>;

    /**
     * A handler for the messageChange event.
     */
    @Output() messageChange: EventEmitter<string>;

    /**
     * A handler for the minHeightChange event.
     */
    @Output() minHeightChange: EventEmitter<number | Function | string>;

    /**
     * A handler for the minWidthChange event.
     */
    @Output() minWidthChange: EventEmitter<number | Function | string>;

    /**
     * A handler for the positionChange event.
     */
    @Output() positionChange: EventEmitter<DevExpress.positionConfig | string>;

    /**
     * A handler for the rtlEnabledChange event.
     */
    @Output() rtlEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the shadingChange event.
     */
    @Output() shadingChange: EventEmitter<boolean>;

    /**
     * A handler for the shadingColorChange event.
     */
    @Output() shadingColorChange: EventEmitter<string>;

    /**
     * A handler for the tabIndexChange event.
     */
    @Output() tabIndexChange: EventEmitter<number>;

    /**
     * A handler for the typeChange event.
     */
    @Output() typeChange: EventEmitter<string>;

    /**
     * A handler for the visibleChange event.
     */
    @Output() visibleChange: EventEmitter<boolean>;

    /**
     * A handler for the widthChange event.
     */
    @Output() widthChange: EventEmitter<number | Function | string>;





    constructor(elementRef: ElementRef, ngZone: NgZone, templateHost: DxTemplateHost,
            _watcherHelper: WatcherHelper, optionHost: NestedOptionHost,
            transferState: TransferState,
            @Inject(PLATFORM_ID) platformId: any) {

        super(elementRef, ngZone, templateHost, _watcherHelper, transferState, platformId);

        this._createEventEmitters([
            { subscribe: 'contentReady', emit: 'onContentReady' },
            { subscribe: 'disposing', emit: 'onDisposing' },
            { subscribe: 'hidden', emit: 'onHidden' },
            { subscribe: 'hiding', emit: 'onHiding' },
            { subscribe: 'initialized', emit: 'onInitialized' },
            { subscribe: 'optionChanged', emit: 'onOptionChanged' },
            { subscribe: 'showing', emit: 'onShowing' },
            { subscribe: 'shown', emit: 'onShown' },
            { emit: 'accessKeyChange' },
            { emit: 'animationChange' },
            { emit: 'closeOnBackButtonChange' },
            { emit: 'closeOnClickChange' },
            { emit: 'closeOnOutsideClickChange' },
            { emit: 'closeOnSwipeChange' },
            { emit: 'contentTemplateChange' },
            { emit: 'deferRenderingChange' },
            { emit: 'displayTimeChange' },
            { emit: 'elementAttrChange' },
            { emit: 'focusStateEnabledChange' },
            { emit: 'heightChange' },
            { emit: 'hintChange' },
            { emit: 'hoverStateEnabledChange' },
            { emit: 'maxHeightChange' },
            { emit: 'maxWidthChange' },
            { emit: 'messageChange' },
            { emit: 'minHeightChange' },
            { emit: 'minWidthChange' },
            { emit: 'positionChange' },
            { emit: 'rtlEnabledChange' },
            { emit: 'shadingChange' },
            { emit: 'shadingColorChange' },
            { emit: 'tabIndexChange' },
            { emit: 'typeChange' },
            { emit: 'visibleChange' },
            { emit: 'widthChange' }
        ]);
        optionHost.setHost(this);
    }

    protected _createInstance(element, options) {
        return new DxToast(element, options);
    }

    ngOnDestroy() {
        this._destroyWidget();
    }


}

@NgModule({
  imports: [
    DxoAnimationModule,
    DxoHideModule,
    DxoShowModule,
    DxoPositionModule,
    DxoAtModule,
    DxoBoundaryOffsetModule,
    DxoCollisionModule,
    DxoMyModule,
    DxoOffsetModule,
    DxIntegrationModule,
    DxTemplateModule,
    BrowserTransferStateModule
  ],
  declarations: [
    DxToastComponent
  ],
  exports: [
    DxToastComponent,
    DxoAnimationModule,
    DxoHideModule,
    DxoShowModule,
    DxoPositionModule,
    DxoAtModule,
    DxoBoundaryOffsetModule,
    DxoCollisionModule,
    DxoMyModule,
    DxoOffsetModule,
    DxTemplateModule
  ]
})
export class DxToastModule { }
